import {
  useSessionId,
  useEppoClient,
  useEppoDefaultSubjectAttributes,
} from '@moonpig/web-core-utils'
import { useMemo } from 'react'
import { logger } from '@moonpig/web-core-monitoring'
import { Flag, VariationType } from '@eppo/js-client-sdk-common'
import {
  useEppoCustomerId,
  useEppoExperimentOverrides,
} from '@moonpig/web-core-utils/src/eppoContext/EppoContext'
import { Attributes } from '../../shared/DefaultSubjectAttributes'
import { resolveEppoSubjectKey } from '../../shared/resolveEppoSubjectKey'

export const useExperiment = <R>(
  experimentKey: string,
  args: { fallback: R; subjectAttributes?: Attributes },
): R => {
  const overrides = useEppoExperimentOverrides()
  const override = overrides?.[experimentKey]

  const { sessionId } = useSessionId()
  const customerId = useEppoCustomerId()
  const eppoClient = useEppoClient()

  const eppoDefaultSubjectAttributes = useEppoDefaultSubjectAttributes()

  const assignedVariation = useMemo(() => {
    if (!eppoClient) {
      logger.fixToday('useEppoExperiment: Client is not defined')
      return args.fallback
    }
    if (!eppoDefaultSubjectAttributes) {
      logger.fixToday(
        'useEppoExperiment: Default subject attributes are not defined',
      )
      return args.fallback
    }

    const flagConfiguration: Flag | undefined =
      eppoClient.getFlagConfigurations()[experimentKey]

    const subjectKey = flagConfiguration
      ? resolveEppoSubjectKey(flagConfiguration, sessionId, customerId)
      : sessionId

    if (!subjectKey) return args.fallback

    const type = flagConfiguration?.variationType

    switch (type) {
      case VariationType.STRING:
        if (override) return override
        return eppoClient.getStringAssignment(
          experimentKey,
          subjectKey,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
          },
          args.fallback as string,
        )

      case VariationType.BOOLEAN:
        if (override) return override === 'true'
        return eppoClient.getBooleanAssignment(
          experimentKey,
          subjectKey,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
          },
          args.fallback as boolean,
        )

      case VariationType.INTEGER:
        if (override) return Number(override)
        return eppoClient.getIntegerAssignment(
          experimentKey,
          subjectKey,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
          },
          args.fallback as number,
        )
      case VariationType.NUMERIC:
        if (override) return Number(override)
        return eppoClient.getNumericAssignment(
          experimentKey,
          subjectKey,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
          },
          args.fallback as number,
        )
      case VariationType.JSON:
        if (override) return JSON.parse(override)
        return eppoClient.getJSONAssignment(
          experimentKey,
          subjectKey,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
          },
          args.fallback as object,
        )
      case undefined:
        if (override) return override === 'false' ? false : override
        return args.fallback
      default:
        logger.fixToday(
          `useEppoExperiment: Experiment ${experimentKey} has an unsupported variation type: ${type}`,
        )
        return args.fallback
    }
  }, [
    experimentKey,
    eppoClient,
    eppoDefaultSubjectAttributes,
    sessionId,
    customerId,
    override,
    args,
  ])

  return assignedVariation
}
